import { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { cn } from "@/lib/utils/cn";

// --- Types ---

export type ViewMode = 'grid' | 'list';

type DataViewContextType = {
  viewMode: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  isLoading: boolean;
  isEmpty: boolean;
  
  // Multi-Select
  isMultiSelectMode: boolean;
  setIsMultiSelectMode: (active: boolean) => void;
  selectedIds: string[];
  onToggleSelect: (id: string, multiSelect?: boolean) => void;
  onMultiSelect?: (ids: string[]) => void;
  onSelectionDelete?: () => void;
  clearSelection: () => void;
};

const DataViewContext = createContext<DataViewContextType | undefined>(undefined);

const DEFAULT_CONTEXT: DataViewContextType = {
  viewMode: 'grid',
  isLoading: false,
  isEmpty: false,
  isMultiSelectMode: false,
  setIsMultiSelectMode: () => {},
  selectedIds: [],
  onToggleSelect: () => {},
  clearSelection: () => {}
};

export const useDataView = () => {
  const context = useContext(DataViewContext);
  if (!context) {
    return DEFAULT_CONTEXT;
  }
  return context;
};

interface DataViewProps {
  children: ReactNode;
  /* Current view mode (grid or list). If controlled, provide this. */
  viewMode?: ViewMode;
  /* Callback when view mode changes. */
  onViewModeChange?: (mode: ViewMode) => void;
  /* Loading state */
  isLoading?: boolean;
  /* Whether the data list is empty (used to show empty state if not loading) */
  isEmpty?: boolean;
  className?: string;

  // Multi-Select
  selectedIds?: string[];
  onMultiSelect?: (ids: string[]) => void;
  onSelectionDelete?: () => void;
}

/**
 * DataView Root Component
 * Provides context and layout structure for data visualization.
 */
export function DataView({
  children,
  viewMode = 'grid',
  onViewModeChange,
  isLoading = false,
  isEmpty = false,
  className,
  selectedIds = [],
  onMultiSelect,
  onSelectionDelete
}: DataViewProps) {
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const onToggleSelect = useCallback((id: string, multiSelectKey = false) => {
    if (!onMultiSelect) return;
    
    // If not in multi-select mode but holding key, enter mode
    if (!isMultiSelectMode && multiSelectKey) {
      setIsMultiSelectMode(true);
    }

    const isSelected = selectedIds.includes(id);
    let newSelected: string[];
    
    if (isSelected) {
      newSelected = selectedIds.filter(selectedId => selectedId !== id);
    } else {
      newSelected = [...selectedIds, id];
    }
    
    onMultiSelect(newSelected);
  }, [onMultiSelect, selectedIds, isMultiSelectMode]);

  const clearSelection = useCallback(() => {
    onMultiSelect?.([]);
    setIsMultiSelectMode(false);
  }, [onMultiSelect]);

  // Handle Escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isMultiSelectMode || selectedIds.length > 0)) {
        clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMultiSelectMode, selectedIds.length, clearSelection]);

  const contextValue: DataViewContextType = {
    viewMode,
    onViewModeChange,
    isLoading,
    isEmpty,
    isMultiSelectMode,
    setIsMultiSelectMode,
    selectedIds,
    onToggleSelect,
    onMultiSelect,
    onSelectionDelete,
    clearSelection
  };

  return (
    <DataViewContext.Provider value={contextValue}>
      <div className={cn("min-h-full w-full flex flex-col bg-gray-50", className)}>
        {children}
      </div>
    </DataViewContext.Provider>
  );
}

/**
 * Main content area wrapper.
 * Handles scrolling.
 */
interface DataViewLayoutProps {
  children: ReactNode;
  className?: string;
}

export function DataViewLayout({ children, className }: DataViewLayoutProps) {
  return (
    <div className={cn("flex-1 p-4 sm:p-6 w-full max-w-7xl mx-auto min-h-[100vh] lg:min-h-0", className)}>
      {children}
    </div>
  );
}
